import {
  UserChatMessage,
  AssistantChatMessage,
  ChatSession,
  User,
  Organization,
  RemoteAgent,
  Memory,
  ChatMessage,
} from '@/types'

const API_BASE_URL = 'http://localhost:8081'
let authToken: string | null = null

export const setAuthToken = (token: string | null) => {
  authToken = token
}

const getHeaders = () => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }
  return headers
}

const handleResponse = async (response: Response) => {
  if (response.ok) {
    if (response.status === 204) return null
    const text = await response.text()
    return text ? JSON.parse(text) : {}
  }

  const errorBody = await response.text()
  let errorJson: any = {}
  try {
    errorJson = JSON.parse(errorBody)
  } catch (e) {
    // ignore
  }

  const errorMessage =
    errorJson.detail ||
    errorJson.message ||
    errorJson.error_message ||
    `HTTP error! status: ${response.status}`
  throw new Error(
    Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage,
  )
}

const normalizeMessage = (msg: any): ChatMessage => {
  let role = msg.role
  if (!role) {
    if (msg.metadata?.user_id || msg.user_id || msg.type === 'user') {
      role = 'user'
    } else {
      role = 'assistant'
    }
  }

  const normalizedMsg = { ...msg, role }

  if (normalizedMsg.role === 'assistant' && normalizedMsg.status === 'ok') {
    normalizedMsg.status = 'completed'
  }

  return normalizedMsg as ChatMessage
}

// Auth
export const loginWithGoogle = async (
  googleJwt: string,
): Promise<{ token: string; user: User }> => {
  const response = await fetch(`${API_BASE_URL}/auth/google`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ token: googleJwt }),
  })
  return handleResponse(response)
}

// User
export const getCurrentUser = async (): Promise<User> => {
  const response = await fetch(`${API_BASE_URL}/api/user`, {
    headers: getHeaders(),
  })
  return handleResponse(response)
}

export const updateCurrentUser = async (
  userData: Partial<User>,
): Promise<User> => {
  const response = await fetch(`${API_BASE_URL}/api/user`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(userData),
  })
  return handleResponse(response)
}

// Organizations
export const getOrganizations = async (): Promise<Organization[]> => {
  const response = await fetch(`${API_BASE_URL}/api/organizations`, {
    headers: getHeaders(),
  })
  return handleResponse(response)
}

export const createOrganization = async (
  orgData: Partial<Organization>,
): Promise<Organization> => {
  const response = await fetch(`${API_BASE_URL}/api/organizations`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(orgData),
  })
  return handleResponse(response)
}

export const getOrganizationById = async (
  id: string,
): Promise<Organization> => {
  const response = await fetch(`${API_BASE_URL}/api/organizations/${id}`, {
    headers: getHeaders(),
  })
  return handleResponse(response)
}

export const updateOrganization = async (
  id: string,
  orgData: Partial<Organization>,
): Promise<Organization> => {
  const response = await fetch(`${API_BASE_URL}/api/organizations/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(orgData),
  })
  return handleResponse(response)
}

// Remote Agents
export const getRemoteAgents = async (): Promise<RemoteAgent[]> => {
  const response = await fetch(`${API_BASE_URL}/api/remote-agents`, {
    headers: getHeaders(),
  })
  return handleResponse(response)
}

export const registerRemoteAgent = async (
  agentData: Partial<RemoteAgent>,
): Promise<RemoteAgent> => {
  const response = await fetch(`${API_BASE_URL}/api/remote-agents`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(agentData),
  })
  return handleResponse(response)
}

export const getRemoteAgentById = async (id: string): Promise<RemoteAgent> => {
  const response = await fetch(`${API_BASE_URL}/api/remote-agents/${id}`, {
    headers: getHeaders(),
  })
  return handleResponse(response)
}

export const updateRemoteAgent = async (
  id: string,
  agentData: Partial<RemoteAgent>,
): Promise<RemoteAgent> => {
  const response = await fetch(`${API_BASE_URL}/api/remote-agents/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(agentData),
  })
  return handleResponse(response)
}

export const deleteRemoteAgent = async (id: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/remote-agents/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  })
  await handleResponse(response)
}

// Memories
export const getMemories = async (orgId: string): Promise<Memory[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/organizations/${orgId}/memories`,
    { headers: getHeaders() },
  )
  return handleResponse(response)
}

export const addMemory = async (
  orgId: string,
  memoryData: { content: string },
): Promise<Memory> => {
  const payload = {
    id: null,
    organizationId: orgId,
    content: memoryData.content,
  }
  const response = await fetch(
    `${API_BASE_URL}/api/organizations/${orgId}/memories`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    },
  )
  return handleResponse(response)
}

export const updateMemory = async (
  orgId: string,
  memoryId: string,
  memoryData: Memory,
): Promise<Memory> => {
  const response = await fetch(
    `${API_BASE_URL}/api/organizations/${orgId}/memories/${memoryId}`,
    {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(memoryData),
    },
  )
  return handleResponse(response)
}

export const deleteMemory = async (
  orgId: string,
  memoryId: string,
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/organizations/${orgId}/memories/${memoryId}`,
    { method: 'DELETE', headers: getHeaders() },
  )
  await handleResponse(response)
}

// Assistant Chat
export const sendMessage = async (
  message: UserChatMessage,
): Promise<AssistantChatMessage> => {
  const response = await fetch(`${API_BASE_URL}/assistant/mcp`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(message),
  })
  return handleResponse(response)
}

export const getSession = async (id: string): Promise<ChatSession | null> => {
  const response = await fetch(`${API_BASE_URL}/assistant/mcp/${id}`, {
    headers: getHeaders(),
  })
  if (response.status === 404) {
    return null
  }
  const rawSession = await handleResponse(response)

  if (!rawSession || !rawSession.id) {
    return null
  }

  const messages: ChatMessage[] = (rawSession.ui_chat_messages || []).map(
    normalizeMessage,
  )

  return {
    id: rawSession.id,
    messages,
    last_interaction: rawSession.last_interaction,
  }
}

export const getSessions = async (): Promise<ChatSession[]> => {
  const response = await fetch(`${API_BASE_URL}/assistant/sessions`, {
    headers: getHeaders(),
  })

  if (response.status === 204 || response.status === 404) {
    return []
  }

  const responseData = await handleResponse(response)

  if (responseData && Array.isArray(responseData.sessions)) {
    return responseData.sessions.map((session: any) => ({
      id: session.id,
      messages: (session.ui_chat_messages || []).map(normalizeMessage),
      last_interaction: session.last_interaction,
    }))
  }

  return []
}
